// 🔄 기기별 관리 훅 - 개별 기기 상태/가격 업데이트 및 전체 신청서 동기화
import { useState, useCallback, useMemo } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../../firebase';
import type { Application, DeviceInfo } from '../../../types/application';
import { 
  calculateApplicationStatus, 
  calculateTotalPrices,
  validateDeviceStatusChange,
  synchronizeApplicationData 
} from '../../../../../utils/applicationStatusCalculator';

interface UseDeviceManagementReturn {
  loading: boolean;
  hasChanges: boolean;
  handleDeviceUpdate: (deviceId: string, updates: Partial<DeviceInfo>) => Promise<void>;
  handleBatchDeviceUpdate: (batchUpdates: Record<string, Partial<DeviceInfo>>) => Promise<void>;
  syncApplicationData: () => Promise<void>;
  getDeviceById: (deviceId: string) => DeviceInfo | undefined;
  getApplicationSummary: () => {
    totalDevices: number;
    pendingCount: number;
    completedCount: number;
    rejectedCount: number;
    totalEstimatedPrice: number;
    totalFinalPrice: number;
  };
}

export const useDeviceManagement = (
  application: Application | null,
  onUpdate?: (updatedApplication: Partial<Application>) => void,
  onError?: (error: string) => void
): UseDeviceManagementReturn => {
  const [loading, setLoading] = useState(false);


  // 📊 현재 신청서 요약 정보 계산
  const getApplicationSummary = useCallback(() => {
    if (!application?.devices) {
      return {
        totalDevices: 0,
        pendingCount: 0,
        completedCount: 0,
        rejectedCount: 0,
        totalEstimatedPrice: 0,
        totalFinalPrice: 0
      };
    }

    const devices = application.devices;
    const { total_price_offer, total_final_price } = calculateTotalPrices(devices);
    
    return {
      totalDevices: devices.length,
      pendingCount: devices.filter(d => d.status === '접수완료').length,
      completedCount: devices.filter(d => d.status === '완료').length,
      rejectedCount: devices.filter(d => d.status === '반송').length,
      totalEstimatedPrice: total_price_offer,
      totalFinalPrice: total_final_price
    };
  }, [application?.devices]);

  // 🔍 기기 ID로 기기 찾기
  const getDeviceById = useCallback((deviceId: string): DeviceInfo | undefined => {
    return application?.devices?.find(device => device.id === deviceId);
  }, [application?.devices]);

  // 🎯 변경사항 감지
  const hasChanges = useMemo(() => {
    if (!application?.devices) return false;
    
    // 새로운 구조와 기존 구조 비교
    const currentData = synchronizeApplicationData(application.devices);
    
    return (
      application.status !== currentData.status ||
      application.total_price_offer !== currentData.total_price_offer ||
      application.total_final_price !== currentData.total_final_price
    );
  }, [application]);

  // 🔄 개별 기기 업데이트 핸들러
  const handleDeviceUpdate = useCallback(async (deviceId: string, updates: any) => {
    // 기기 순서 확인
    const deviceIndex = application?.devices?.findIndex(d => d.id === deviceId) ?? -1;
    console.log(`🚀 [useDeviceManagement] 기기 ${deviceIndex + 1} 업데이트 시작`);
    console.log(`🚀 [useDeviceManagement] deviceId: ${deviceId}`);
    console.log(`🚀 [useDeviceManagement] 업데이트 내용:`, updates);
    console.log(`🚀 [useDeviceManagement] price_offer: ${updates.price_offer} (${typeof updates.price_offer})`);
    console.log(`🚀 [useDeviceManagement] final_price: ${updates.final_price} (${typeof updates.final_price})`);
    
    // 🎯 배치 업데이트 감지
    const isBatch = !!updates._batchDevices;
    if (isBatch) {
      console.log(`🎯 [배치 처리] 전체 기기 배열 업데이트 감지`);
      console.log(`🎯 [배치 처리] 기기 개수: ${updates._batchDevices.length}`);
    }
    
    if (!application?.id || !application.devices) {
      console.error(`❌ [useDeviceManagement] 신청서 정보 없음`);
      onError?.('신청서 정보를 찾을 수 없습니다.');
      return;
    }
    
    setLoading(true);
    try {
      // 1. 현재 기기 정보 확인
      const currentDevice = application.devices.find(d => d.id === deviceId);
      if (!currentDevice) {
        throw new Error('기기를 찾을 수 없습니다.');
      }

      // 2. 상태 변경 시 유효성 검증
      if (updates.status) {
        const validation = validateDeviceStatusChange(currentDevice, updates.status);
        if (!validation.isValid) {
          throw new Error(validation.error);
        }
      }

      // 3. 기기 업데이트 적용
      let updatedDevices;
      
      if (updates._batchDevices) {
        // 🎯 배치 업데이트: 전체 기기 배열 사용
        console.log(`🎯 [배치 처리] 전체 기기 배열로 교체`);
        updatedDevices = updates._batchDevices;
        
        // _batchDevices는 Firestore에 저장하지 않음
        const { _batchDevices, ...cleanUpdates } = updates;
        updates = cleanUpdates;
      } else {
        // 🔄 개별 업데이트: 기존 로직
        console.log(`🔄 [개별 처리] 단일 기기 업데이트`);
        updatedDevices = application.devices.map(device => {
          if (device.id === deviceId) {
            let deviceUpdates = { ...device, ...updates, updated_at: new Date() };
            
            // ✅ 요구사항: 기기 상태를 '완료'로 변경할 때 final_price가 없으면 price_offer로 보정
            if (updates.status === '완료' && (!deviceUpdates.final_price || deviceUpdates.final_price === 0)) {
              deviceUpdates.final_price = deviceUpdates.price_offer;
            }
            
            return deviceUpdates;
          }
          return device;
        });
      }
      
      // 4. 전체 상태와 가격 자동 계산
      const syncedData = synchronizeApplicationData(updatedDevices);
      
      // 5. Firestore 업데이트
      const updateType = isBatch ? '배치' : '개별';
      console.log(`📊 [${updateType} 처리] 업데이트된 기기 배열 (${updatedDevices.length}개):`);
      updatedDevices.forEach((device: DeviceInfo, index: number) => {
        console.log(`📊 [기기 ${index + 1}] ${device.deviceModel}: price_offer=${device.price_offer}, final_price=${device.final_price}`);
      });
      console.log(`🧮 [${updateType} 처리] 계산된 전체 상태:`, syncedData);
      console.log(`🧮 [${updateType} 처리] total_price_offer: ${syncedData.total_price_offer}, total_final_price: ${syncedData.total_final_price}`);
      
      const applicationRef = doc(db, 'applications', application.id);
      const updateData = {
        devices: updatedDevices,
        status: syncedData.status as Application['status'],
        total_price_offer: syncedData.total_price_offer,
        total_final_price: syncedData.total_final_price,
        // 하위 호환성을 위한 기존 필드 유지
        price_offer: syncedData.price_offer,
        final_price: syncedData.final_price,
        updated_at: serverTimestamp(),
      };
      
      console.log(`💾 [${updateType} 처리] Firestore 업데이트 준비완료`);
      console.log(`💾 [${updateType} 처리] 첫번째 기기 최종 확인: price_offer=${updatedDevices[0]?.price_offer}, final_price=${updatedDevices[0]?.final_price}`);
      
      await updateDoc(applicationRef, updateData);
      console.log(`✅ [${updateType} 처리] Firestore 업데이트 완료`);
      
      // 6. 상위 컴포넌트에 업데이트 알림
      if (onUpdate) {
        onUpdate({
          ...updateData,
          devices: updatedDevices
        });
      }
      
    } catch (error) {
      console.error('Device update error:', error);
      const errorMessage = error instanceof Error ? error.message : '기기 업데이트에 실패했습니다.';
      onError?.(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [application, onUpdate, onError]);

  // 🔄 기기 일괄 업데이트 핸들러
  const handleBatchDeviceUpdate = useCallback(async (
    batchUpdates: Record<string, Partial<DeviceInfo>>
  ) => {
    if (!application?.id || !application.devices) {
      onError?.('신청서 정보를 찾을 수 없습니다.');
      return;
    }
    
    setLoading(true);
    try {
      let updatedDevices = [...application.devices];
      const validationErrors: string[] = [];
      const successfulUpdates: string[] = [];
      
      // 1. 모든 기기 업데이트 적용 및 유효성 검증
      console.log(`🚀 [배치 업데이트] ${Object.keys(batchUpdates).length}개 기기 배치 업데이트 시작`);
      
      for (const [deviceId, updates] of Object.entries(batchUpdates)) {
        const currentDevice = updatedDevices.find(d => d.id === deviceId);
        if (!currentDevice) {
          console.warn(`기기 ${deviceId}를 찾을 수 없습니다.`);
          continue;
        }
        
        // 🎯 업데이트될 기기 정보 미리 적용 (검증용)
        const deviceWithUpdates = {
          ...currentDevice,
          ...updates
        };
        
        // ✅ 요구사항: 기기 상태를 '완료'로 변경할 때 final_price가 없으면 price_offer로 보정
        if (updates.status === '완료' && (!deviceWithUpdates.final_price || deviceWithUpdates.final_price === 0)) {
          deviceWithUpdates.final_price = deviceWithUpdates.price_offer;
        }
        
        // 상태 변경 시 유효성 검증 (업데이트된 정보로 검증)
        if (updates.status) {
          const validation = validateDeviceStatusChange(deviceWithUpdates, updates.status);
          if (!validation.isValid) {
            validationErrors.push(`${currentDevice.deviceModel}: ${validation.error}`);
            continue;
          }
        }
        
        // 유효성 검증 통과한 기기 업데이트 적용
        const deviceIndex = updatedDevices.findIndex(d => d.id === deviceId);
        if (deviceIndex !== -1) {
          updatedDevices[deviceIndex] = deviceWithUpdates;
          successfulUpdates.push(deviceId);
        }
      }
      
      // 🚨 검증 실패가 있을 경우 에러 처리
      if (validationErrors.length > 0) {
        const errorMessage = `다음 기기들의 처리가 실패했습니다:\n${validationErrors.join('\n')}`;
        console.error(`❌ 검증 실패: ${validationErrors.length}개 기기`);
        
        // 모든 기기가 실패한 경우 완전 실패로 처리
        if (successfulUpdates.length === 0) {
          onError?.(errorMessage);
          return;
        }
        
        // 일부만 실패한 경우 경고 메시지와 함께 성공한 것들은 처리
        console.warn(`⚠️ 부분 성공: ${successfulUpdates.length}개 성공, ${validationErrors.length}개 실패`);
      }
      
      // 2. 전체 상태와 가격 자동 계산
      const syncedData = synchronizeApplicationData(updatedDevices);
      
      // 3. Firestore 업데이트
      console.log(`📊 [배치 업데이트] 최종 업데이트된 기기 배열:`);
      updatedDevices.forEach((device, index) => {
        console.log(`📊 [기기 ${index + 1}] ${device.deviceModel}: price_offer=${device.price_offer}, final_price=${device.final_price}`);
      });
      
      const applicationRef = doc(db, 'applications', application.id);
      const updateData = {
        devices: updatedDevices,
        status: syncedData.status as Application['status'],
        total_price_offer: syncedData.total_price_offer,
        total_final_price: syncedData.total_final_price,
        price_offer: syncedData.price_offer,
        final_price: syncedData.final_price,
        updated_at: serverTimestamp(),
      };
      
      console.log(`💾 [배치 업데이트] Firestore 업데이트 시작`);
      console.log(`💾 [배치 업데이트] total_price_offer: ${syncedData.total_price_offer}, total_final_price: ${syncedData.total_final_price}`);
      
      await updateDoc(applicationRef, updateData);
      
      // 4. 상위 컴포넌트에 업데이트 알림
      if (onUpdate) {
        onUpdate({
          ...updateData,
          devices: updatedDevices
        });
      }
      
      console.log(`✅ [배치 업데이트] 완료 - 성공: ${successfulUpdates.length}개, 실패: ${validationErrors.length}개`);
      
      // 🎯 부분 실패 시 추가 정보 제공
      if (validationErrors.length > 0 && successfulUpdates.length > 0) {
        console.log(`✅ 성공한 기기들:`, successfulUpdates);
        console.log(`❌ 실패한 기기들:`, validationErrors);
      }
      
    } catch (error) {
      console.error('Batch device update error:', error);
      const errorMessage = error instanceof Error ? error.message : '기기 일괄 업데이트에 실패했습니다.';
      onError?.(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [application, onUpdate, onError]);

  // 🔄 신청서 데이터 동기화 (수동 호출용)
  const syncApplicationData = useCallback(async () => {
    if (!application?.id || !application.devices) {
      onError?.('신청서 정보를 찾을 수 없습니다.');
      return;
    }
    
    setLoading(true);
    try {
      const syncedData = synchronizeApplicationData(application.devices);
      
      const applicationRef = doc(db, 'applications', application.id);
      const updateData = {
        status: syncedData.status as Application['status'],
        total_price_offer: syncedData.total_price_offer,
        total_final_price: syncedData.total_final_price,
        price_offer: syncedData.price_offer,
        final_price: syncedData.final_price,
        updated_at: serverTimestamp(),
      };
      
      await updateDoc(applicationRef, updateData);
      
      if (onUpdate) {
        onUpdate(updateData);
      }
      
      console.log('✅ 신청서 데이터 동기화 완료');
      
    } catch (error) {
      console.error('Sync application data error:', error);
      const errorMessage = error instanceof Error ? error.message : '데이터 동기화에 실패했습니다.';
      onError?.(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [application, onUpdate, onError]);

  return {
    loading,
    hasChanges,
    handleDeviceUpdate,
    handleBatchDeviceUpdate,
    syncApplicationData,
    getDeviceById,
    getApplicationSummary
  };
};